import { UserProfile, ITemplateDynamicInfo } from '../types';
import { resolveHeadshotUrl } from './headshotResolver';

export interface FormData {
  alternatePhone: string;
  bookCover: string;
  bookCover3D: string;
  brokerLogo: File | string | undefined;
  brokerName: string;
  ctaText: string;
  customctaText: string;
  email: string;
  fullName: string;
  headshot: File | string | undefined;
  phone: string;
  website: string;
  websiteCustom: string;
}

/**
 * Format phone number to (XXX) XXX-XXXX format
 */
export function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');

  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Get the title for the current step
 */
export function getStepTitle(currentStep: number): string {
  switch (currentStep) {
    case 1:
      return 'Your Defaults';
    case 2:
      return 'Your Business Info';
    case 3:
      return 'Choose Your Book';
    case 4:
      return 'Your Social Media';
    default:
      return 'Set Up your Defaults';
  }
}

/**
 * Get the title for the current step
 */
export function getStepDescription(currentStep: number): string {
  switch (currentStep) {
    case 1:
      return "We'll use this info to pre-fill your templates so you can save time and look professional-every time. You'll still be able to change anything later if you want.";
    case 2:
      return 'Add your brokerage or business logo and name.';
    case 3:
      return "Select which book and website you'd like to use as your default in your marketing templates.";
    case 4:
      return 'Choose a “call to action” phrase to appear on your social media templates. Invite leads to request your book or let them know how to contact you.';
    default:
      return 'Set Up your Defaults';
  }
}

/**
 * Get initial form data
 */
export function getInitialFormData(): FormData {
  return {
    alternatePhone: '',
    bookCover: '',
    bookCover3D: '',
    brokerLogo: undefined,
    brokerName: '',
    ctaText: '',
    customctaText: '',
    email: '',
    fullName: '',
    headshot: undefined,
    phone: '',
    website: '',
    websiteCustom: '',
  };
}

/**
 * Initialize form data from user profile and template dynamic info
 */
export function initializeFormData(
  userProfile: UserProfile | null,
  templateDynamicInfo: ITemplateDynamicInfo | null
): FormData {
  if (!userProfile) {
    return getInitialFormData();
  }

  const fallbackPhone = formatPhoneNumber(userProfile?.phone ?? '');
  const resolvedPhone = templateDynamicInfo?.phone || fallbackPhone;
  const resolvedHeadshot = templateDynamicInfo?.headshot || userProfile?.imageUrl || undefined;
  const resolvedName = templateDynamicInfo?.fullName || `${userProfile?.firstname} ${userProfile?.lastname}`;
  const resolvedEmail = templateDynamicInfo?.email || userProfile?.email;

  return {
    alternatePhone: templateDynamicInfo?.alternatePhone || '',
    bookCover: templateDynamicInfo?.bookCover || '',
    bookCover3D: templateDynamicInfo?.bookCover3D || '',
    brokerLogo: templateDynamicInfo?.brokerLogo || undefined,
    brokerName: templateDynamicInfo?.brokerName || '',
    ctaText: templateDynamicInfo?.ctaText || '',
    customctaText: '',
    email: resolvedEmail,
    fullName: resolvedName,
    headshot: resolvedHeadshot,
    phone: resolvedPhone,
    website: templateDynamicInfo?.website || '',
    websiteCustom: '',
  };
}

/**
 * Prepare template dynamic data for API submission
 */
export function prepareTemplateDynamicData(formData: FormData) {
  return {
    email: formData.email,
    bookCover: formData.bookCover,
    bookCover3D: formData.bookCover3D,
    brokerLogo: formData.brokerLogo || '',
    brokerName: formData.brokerName || '',
    headshot: resolveHeadshotUrl(formData?.headshot),
    ctaText: formData.ctaText,
    website: formData.website,
    fullName: formData.fullName,
    phone: formData.phone,
    alternatePhone: formData.alternatePhone || '',
  };
}

// /**
//  * Validate Step 1 form data
//  */
// export function validateStep1(formData: FormData): boolean {
//   return !!(formData.fullName && formData.email && formData.phone && formData.headshot);
// }

// /**
//  * Validate Step 2 form data
//  */
// export function validateStep2(formData: FormData): boolean {
//   return !!(formData.brokerName || formData.brokerLogo || formData.website);
// }

// /**
//  * Validate Step 3 form data
//  */
// export function validateStep3(formData: FormData): boolean {
//   return !!(formData.bookCover && formData.bookCover3D);
// }

// /**
//  * Validate Step 4 form data
//  */
// export function validateStep4(formData: FormData): boolean {
//   return !!(formData.ctaText);
// }